import debounce from 'lodash/debounce'
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'

import { TextField } from '../TextField'
import { TooltipWrapper } from '../TextField/TextFieldElements'
import * as S from './RangeInput.styles'

interface RangeFilterProps {
  initialMin?: number
  initialMax?: number
  fromInputTypeIndicator?: string
  toInputTypeIndicator?: string
  label?: ReactNode
  maxRangeValue?: number
  onRangeChange: (minValue?: number, maxValue?: number) => void
  className?: string
  disabled?: boolean
  placeholder?: { min?: string; max?: string }
  tooltipMessage?: string
}

export const RangeInput: FC<RangeFilterProps> = ({
  initialMin,
  initialMax,
  fromInputTypeIndicator = 'MIN',
  toInputTypeIndicator = 'MAX',
  label,
  maxRangeValue,
  onRangeChange,
  disabled,
  className,
  placeholder,
  tooltipMessage,
}) => {
  const [inputFocus, setInputFocus] = useState(false)
  const [minValue, setMinValue] = useState(initialMin)
  const [maxValue, setMaxValue] = useState(initialMax)

  const validateMinValue = () => {
    if (Number(minValue) < 0) return 'Value cannot be negative'

    if (minValue && maxValue && minValue > maxValue) {
      return `Minimum value should not be greater than ${maxValue}`
    }

    if (maxRangeValue && minValue && minValue > maxRangeValue) {
      return `Value is out of range, max value is ${maxRangeValue}`
    }

    return undefined
  }

  const validateMaxValue = () => {
    if (maxValue && maxValue < 0) return 'Value cannot be negative'

    if (maxRangeValue && maxValue && maxValue > maxRangeValue)
      return `Value is out of range, max value is ${maxRangeValue}`

    return undefined
  }

  const handleSubmitValue = debounce(() => {
    onRangeChange(minValue, maxValue)
  }, 300)

  useEffect(() => {
    handleSubmitValue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue, maxValue])

  useEffect(() => {
    if (initialMin === undefined) {
      setMinValue('' as unknown as number)
    }

    if (initialMax === undefined) {
      setMaxValue('' as unknown as number)
    }
  }, [initialMin, initialMax])

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (value === '' || value === undefined) {
      setMinValue(undefined)
    } else {
      setMinValue(+value)
    }
  }

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (value === '' || value === undefined) {
      setMaxValue(undefined)
    } else {
      setMaxValue(+value)
    }
  }

  const getLeftInputIndicator = (inputTypeIndicator?: string) =>
    inputTypeIndicator && <S.Label>{inputTypeIndicator}</S.Label>

  const hasError = !!validateMinValue() || !!validateMaxValue()

  const minRef = useRef<HTMLInputElement>(null)

  const inputActions = {
    onFocus: () => setInputFocus(true),
    onBlur: () => setInputFocus(false),
  }

  return (
    <S.Container className={className} $hasError={hasError} $isFocused={inputFocus}>
      <S.Label>{label}</S.Label>
      <TooltipWrapper message={tooltipMessage}>
        <S.TextContainer $isDisabled={disabled} $hasError={!!validateMinValue()}>
          <TextField.Small
            ref={minRef}
            value={minValue}
            beforeValue={getLeftInputIndicator(fromInputTypeIndicator)}
            placeholder={placeholder?.min}
            onChange={handleMinChange}
            type="number"
            error={validateMinValue()}
            disabled={disabled}
            {...inputActions}
          />
          <TextField.Small
            value={maxValue}
            beforeValue={getLeftInputIndicator(toInputTypeIndicator)}
            placeholder={placeholder?.max}
            onChange={handleMaxChange}
            type="number"
            error={validateMaxValue()}
            disabled={disabled}
            {...inputActions}
          />
        </S.TextContainer>
      </TooltipWrapper>
    </S.Container>
  )
}
